import {Component, computed, inject, OnDestroy, signal, Signal} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {Observable, Subscription, timer} from 'rxjs';
import {map} from 'rxjs/operators';
import {Dialog} from '@angular/cdk/dialog';
import {LoginDialogComponent, LoginDialogData, LoginField, LoginOutcome} from '../login-dialog/login-dialog.component';

import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {ContextService} from '../../services/context.service';
import {ProcessButtonService} from '../../services/process-button.service';
import {ProcessButtonDirective} from '../../process-button.directive';
import {Organization} from '../../types-repository';
import {GET_PORTAL_DETAIL, GET_PORTAL_PROCESSES, GET_PORTALS, IMPORT_FILE, LOGIN, SUBMIT_MFA}
  from '../../queries-signiant';
import {Portal, PortalEntry, Process} from '../../types-signiant';

type PortalDetail = Portal;
import {PortalBrowserComponent} from './portal-browser.component';
import {DateComponent} from '../../date/date.component';
import {IsAllowedAnywherePipe} from '../../pipes/has-action-pipe';

/** A transfer process, flattened for the template: the direction-specific bits
 *  (percent, destination, …) live in the process' `data` JSON, exactly as they
 *  do for repository/workflow processes.
 *
 *  `subject` is what the process is about, and it is the key both directions'
 *  tables look a process up by: the portal path for an import, the
 *  publish-bucket URI for an export. */
interface ProcessRow extends Process {
  percent: number;
  subject: string;
  fileName: string;
  destination?: string;
}

/**
 * A download capability is paused by dating its high-water mark into the future:
 * nothing can be newer than the mark, so nothing is imported. There is no
 * separate flag to read, which is the point — one mechanism, so nothing can
 * disagree with it.
 */
export function isPaused(watermark?: string | null): boolean {
  if (!watermark) {
    return false;   // never swept yet; the next sweep adopts a baseline
  }
  const at = Date.parse(watermark);
  return !Number.isNaN(at) && at > Date.now();
}

/** How far along a file is, so a row can render one state instead of many. */
export type ImportState = 'none' | 'running' | 'done' | 'failed';

@Component({
  selector: 'app-content-acquisition-portal',
  templateUrl: './portal-detail.component.html',
  styleUrls: ['./portal-detail.component.scss'],
  imports: [LogoAreaComponent, HeadlineSectionComponent, RouterModule, DateComponent,
    ProcessButtonDirective, IsAllowedAnywherePipe,
    PortalBrowserComponent]
})
export class PortalDetailComponent implements OnDestroy {
  organization: Signal<Organization>;
  portal = signal<PortalDetail | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);

  /** Set once login reports MFA_REQUIRED: the next login asks for the mailed code. The login-in-progress itself lives
   *  server-side, in the portal's secret. */
  mfaPending = signal(false);

  /** Headline; `portal` is pre-seeded from the list, so this is set at once. */
  portalName = computed(() => this.portal()?.name || this.portalId);

  /** Not private: the template hands it to the shared browser component. */
  readonly portalId: string;
  private idleTicks = 0;
  private poll?: Subscription;
  private apollo = inject(Apollo);
  private dialog = inject(Dialog);
  private processButtons = inject(ProcessButtonService);

  processes = computed<ProcessRow[]>(() =>
    (this.portal()?.processes ?? []).map(p => {
      let data: any = {};
      try {
        data = p.data ? JSON.parse(p.data) : {};
      } catch {
        data = {};
      }
      // An import is about a portal path, an export about a publish-bucket
      // object. Whichever it is, it is the key its table looks it up by.
      const subject: string = data.filePath ?? data.sourceUri ?? '';
      const shown: string = data.targetPath ?? subject;
      return {
        ...p,
        percent: Number(data.percent ?? 0),
        subject,
        fileName: shown ? shown.split('/').pop()! : '—',
        destination: data.destination,
      };
    }));

  /** Downloads and uploads, split by what the process actually did.
   *
   *  `action` is on every process row, so this needs no extra query — but both
   *  lists come out of the SAME 30 rows. On a busy bidirectional portal one
   *  direction can therefore crowd the other out of the window. Fixing that
   *  properly means a GSI on (portal_id, action, start_date); filtering after the
   *  DynamoDB limit would not help, which is the same trap docs/TODO.md item 4
   *  records for the state filter. */
  downloads = computed(() => this.processes().filter(p => p.action !== 'export'));
  uploads = computed(() => this.processes().filter(p => p.action === 'export'));

  /** Newest process per subject — this is what makes the transfer button
   *  process aware. `processes` comes back newest-first, so the first wins. */
  private latestByFile = computed<Map<string, ProcessRow>>(() => {
    const map = new Map<string, ProcessRow>();
    for (const process of this.processes()) {
      if (process.subject && !map.has(process.subject)) {
        map.set(process.subject, process);
      }
    }
    return map;
  });

  /**
   * Which halves of the page to show. A portal can have both — West
   * Entertainment hands us files in one folder and takes ours in another — so
   * these are not alternatives.
   *
   * `loaded` stays its own state on purpose: before the portal arrives both
   * capabilities read absent, and rendering "no delivery side" is a claim we
   * cannot yet make. That is what once flashed an Import button on a delivery
   * portal.
   */
  loaded = computed(() => this.portal() !== null);
  hasUpload = computed(() => !!this.portal()?.upload);
  hasDownload = computed(() => !!this.portal()?.download);
  /** Kept for the template's loading checks. */
  direction = computed(() => (this.loaded() ? 'loaded' : null));

  // The capability objects themselves. A missing one is null, which the template
  // reads as "this portal does not do that" — a fact, not a field that failed to
  // load. Both can be set: West Entertainment hands us files in one folder and
  // takes ours in another.
  uploadConfig = computed(() => this.portal()?.upload ?? null);
  downloadConfig = computed(() => this.portal()?.download ?? null);

  autoDownloadWatermark = computed(() =>
    this.downloadConfig()?.autoDownloadWatermark ?? null);
  /** The mark in the future is the off switch, so "off" is read from it. */
  autoDownloadPaused = computed(() => isPaused(this.autoDownloadWatermark()));

  /** Each capability carries its own cadence; show whichever applies. */
  pollIntervalMinutes = computed(() =>
    this.uploadConfig()?.pollIntervalMinutes
    ?? this.downloadConfig()?.pollIntervalMinutes ?? null);

  constructor(contextService: ContextService, route: ActivatedRoute) {
    this.organization = contextService.organization;
    this.portalId = route.snapshot.params['portal_id'];
    // Paint everything the list page already knows (name, URL, owner org,
    // session status, drop destination, last check) before the detail query —
    // which lists the portal live — comes back and fills in the rest.
    this.portal.set(this.portalFromListCache());
    this.load();
    // An import's percent only moves server-side, so refresh while one runs.
    // With nothing running, still check the process list every half minute —
    // the scheduled sweep (or another operator) can start an import this page
    // knows nothing about, and it should pick up the spinner too. That check
    // is the cheap process-list query, not the full live listing.
    this.poll = timer(8000, 8000).subscribe(() => {
      if (this.processes().some(p => p.state === 'IN_PROGRESS')) {
        this.load(true);
      } else if (++this.idleTicks % 4 === 0) {
        this.refreshProcesses();
      }
    });
  }

  /**
   * This portal as the list page cached it — a subset of the detail fields
   * (GET_PORTALS selects no `browse`, `processes`, `expiresAt` or
   * `pendingFiles`), so the template must keep tolerating missing values. Null
   * when nothing is cached: a deep link or a reloaded page.
   *
   * It does carry both capability blocks, which is why arriving from the list
   * shows the right halves immediately and only a deep link has to wait.
   */
  private portalFromListCache(): PortalDetail | null {
    try {
      const data = this.apollo.use('signiant').client
        .readQuery<{ portals: PortalDetail[] }>({query: GET_PORTALS});
      return data?.portals?.find(p => p.id === this.portalId) ?? null;
    } catch {
      return null;
    }
  }

  ngOnDestroy(): void {
    this.poll?.unsubscribe();
  }

  /** Keyed on the portal path, so a browse entry and a portal file are alike. */
  processFor(file: {path: string}): ProcessRow | undefined {
    return this.latestByFile().get(file.path);
  }

  importState(file: {path: string}): ImportState {
    return this.stateOf(this.processFor(file));
  }

  private stateOf(process: ProcessRow | undefined): ImportState {
    if (!process) {
      return 'none';
    }
    if (process.state === 'IN_PROGRESS') {
      return 'running';
    }
    return process.state === 'SUCCESS' ? 'done' : 'failed';
  }

  /**
   * Log in to the portal, in the login dialog. A session dies after a while and only a human can revive it (the code is
   * mailed), so this belongs in the UI rather than in an operator's terminal.
   */
  openLogin(): void {
    const fields: LoginField[] = this.mfaPending()
      ? [{name: 'code', label: 'Code from the e-mail', type: 'text', value: '', autocomplete: 'one-time-code',
          monospace: true}]
      : this.credentialFields(this.portal()?.status?.username ?? '');
    this.dialog.open<boolean>(LoginDialogComponent, {
      data: {
        headline: `Log in to ${this.portalName()}`,
        intro: this.mfaPending() ? 'Enter the code mailed to you by the portal.' : undefined,
        fields,
        actionName: this.mfaPending() ? 'Confirm' : 'Log in',
        submit: (values: Record<string, string>) => this.submitLogin(values),
      } satisfies LoginDialogData,
    });
  }

  private credentialFields(username: string): LoginField[] {
    return [
      {name: 'username', label: 'Username', type: 'text', value: username, autocomplete: 'username'},
      {name: 'password', label: 'Password', type: 'password', value: '', autocomplete: 'current-password'},
    ];
  }

  /** One submit of the login dialog: the code of the second step, or the username and password of the first. */
  private submitLogin(values: Record<string, string>): Observable<LoginOutcome> {
    if (this.mfaPending()) {
      return this.apollo.use('signiant')
        .mutate<{ submit_mfa: { status: string; message?: string } }>({
          mutation: SUBMIT_MFA, variables: {portalId: this.portalId, code: values['code']},
        })
        .pipe(map(({data}) => {
          if (data?.submit_mfa?.status === 'LOGGED_IN') {
            this.mfaPending.set(false);
            this.load(true);
            return {status: 'done', message: 'Logged in', severity: 'info'} as LoginOutcome;
          }
          // codes expire quickly: the dialog stays up so the code can be retried
          return {status: 'retry', message: data?.submit_mfa?.message ?? 'code rejected', severity: 'danger'} as LoginOutcome;
        }));
    }
    return this.apollo.use('signiant')
      .mutate<{ login: { status: string; message?: string } }>({
        mutation: LOGIN,
        variables: {portalId: this.portalId, username: values['username'].trim(), password: values['password']},
      })
      .pipe(map(({data}) => {
        const result = data?.login;
        if (result?.status === 'MFA_REQUIRED') {
          this.mfaPending.set(true);
          return {
            status: 'next', message: 'Code sent — check your mail', severity: 'info',
            fields: [{name: 'code', label: 'Code from the e-mail', type: 'text', value: '', autocomplete: 'one-time-code',
                      monospace: true}],
          } as LoginOutcome;
        }
        if (result?.status === 'LOGGED_IN') {
          this.load(true);
          return {status: 'done', message: 'Logged in', severity: 'info'} as LoginOutcome;
        }
        return {status: 'retry', message: result?.message ?? 'login failed', severity: 'danger'} as LoginOutcome;
      }));
  }

  /** Stable per-file button id, so the spinner survives navigation. */
  importButtonId(file: {path: string}): string {
    return 'import-' + this.portalId + '-' + file.path;
  }

  /**
   * Start an import. The button spins immediately (setPending), then binds to
   * the returned process: ProcessButtonService/ProcessService poll it and drop
   * the spinner on SUCCESS or FAILED — the same mechanism the CRD and workflow
   * tables use, now that the signiant API is registered as a process source.
   */
  startImport(file: PortalEntry): void {
    const buttonId = this.importButtonId(file);
    this.processButtons.setPending(buttonId);
    this.apollo.use('signiant')
      .mutate<{ import: Process }>({
        mutation: IMPORT_FILE,
        variables: {portalId: this.portalId, folderId: file.folderId, filePath: file.path}
      })
      .subscribe({
        next: ({data}) => {
          const process = data?.import;
          if (!process) {
            this.processButtons.clear(buttonId);
            return;
          }
          this.processButtons.bind(buttonId, process, () => this.load(true));
          this.load(true);
        },
        error: (err) => {
          this.processButtons.clear(buttonId);
          this.error.set(err?.message ?? String(err));
        }
      });
  }

  /**
   * Watch every import that is running, whoever started it — this session's
   * button, another operator, or the scheduled sweep. The file's button then
   * spins exactly as if it had been pressed here, and ProcessService gives the
   * same completion notification. Both services dedupe by process id, so
   * calling this after every refresh is free.
   */
  private adoptRunningProcesses(): void {
    // Imports only. An export has no button to spin since the delivery queue
    // left the page — its progress shows in Recent Uploads.
    for (const process of this.processes()) {
      if (process.state === 'IN_PROGRESS' && process.subject && process.action !== 'export') {
        this.processButtons.bind(
          this.importButtonId({path: process.subject}), process, () => this.load(true));
      }
    }
  }

  /** Process rows only — cheap enough to poll while the page sits idle. */
  private refreshProcesses(): void {
    this.apollo.use('signiant')
      .query<{ portal: PortalDetail }>({
        query: GET_PORTAL_PROCESSES,
        variables: {portalId: this.portalId},
        fetchPolicy: 'network-only'
      })
      .subscribe({
        next: ({data}) => {
          const processes = data?.portal?.processes;
          const current = this.portal();
          if (!processes || !current) {
            return;
          }
          this.portal.set({...current, processes});
          this.adoptRunningProcesses();
        },
        error: () => {  // a transient failure must not disturb the page
        }
      });
  }

  private load(quiet = false): void {
    if (!quiet) {
      this.loading.set(true);
    }
    this.apollo.use('signiant')
      .query<{ portal: PortalDetail }>({
        query: GET_PORTAL_DETAIL,
        variables: {portalId: this.portalId},
        fetchPolicy: 'network-only'
      })
      .subscribe({
        next: ({data}) => {
          this.portal.set(data?.portal ?? null);
          this.loading.set(false);
          this.adoptRunningProcesses();
        },
        error: (err) => {
          this.error.set(err?.message ?? String(err));
          this.loading.set(false);
        }
      });
  }
}

